/**
 * Loading a saved snapshot back as a SCENARIO.
 *
 * Restore does not write and does not "go back to" anything. It takes the inputs
 * a snapshot recorded — the seats, the pins, the hand-picked tier placements —
 * and lays them over TODAY'S book as a what-if. That is the only honest thing it
 * can do: the book has moved on, and the whole point of the exercise is usually
 * to see how much.
 *
 * WHAT MAKES THIS MORE THAN A TYPE CONVERSION. A snapshot is a record of the
 * past, and the present may no longer contain the things it names. Somebody has
 * left the team; a customer has been closed or renamed out of the book. Every
 * one of those has an obvious silent handling — drop the seat, skip the pin —
 * and every one of them would quietly change the answer:
 *
 *   • Dropping a seat whose CSM has left SHRINKS the tier, so the restored
 *     scenario spreads the same book over fewer people and reports a load that
 *     the saved scenario never had. The seat is kept as a VACANCY instead: the
 *     tier really did have that many, and one of them is now unfilled.
 *   • Skipping a pin to somebody who has left is unavoidable — the engine
 *     refuses to distribute to a person the roster does not declare — but a
 *     silent skip turns "this customer was deliberately kept with her" into
 *     "the balancer put it wherever", with no trace.
 *
 * So nothing is dropped quietly. Everything unresolvable comes back in
 * `unresolved`, and the caller is expected to show it.
 */
import type { PinDto, RosterMemberDto, SlotDto, SnapshotDto } from '../api';
import type { Scenario } from './scenario';

export interface RestoreUnresolved {
  /** Seats whose CSM is no longer on the roster. Kept, as vacancies. */
  departedInSeats: { tierKey: string; csmId: string }[];
  /** Pins to somebody no longer on the roster. Cannot be applied. */
  departedInPins: { customerId: string; csmId: string }[];
  /** Pinned customers the current book no longer contains. */
  missingCustomers: string[];
}

export interface RestoreResult {
  scenario: Scenario;
  unresolved: RestoreUnresolved;
  /** True when everything the snapshot named still exists. */
  clean: boolean;
}

export function isRestoreClean(u: RestoreUnresolved): boolean {
  return (
    u.departedInSeats.length === 0 &&
    u.departedInPins.length === 0 &&
    u.missingCustomers.length === 0
  );
}

/**
 * Snapshot inputs → a scenario over the current read set.
 *
 * `roster` and `customerIds` are the PRESENT, passed in rather than looked up,
 * so this stays pure and the "who has left" question is answered against
 * whatever the caller is actually showing.
 */
export function restoreScenario(
  snapshot: Pick<SnapshotDto, 'pins' | 'tierPins' | 'slots'>,
  present: {
    roster: readonly RosterMemberDto[];
    customerIds: ReadonlySet<string>;
  },
): RestoreResult {
  const byId = new Map(present.roster.map((m) => [m.id, m]));

  const departedInSeats: RestoreUnresolved['departedInSeats'] = [];
  const departedInPins: RestoreUnresolved['departedInPins'] = [];
  const missingCustomers: string[] = [];

  // ── seats ────────────────────────────────────────────────────────────────
  // Tier order is not carried by the snapshot's `slots` object and does not need
  // to be: the engine keys seats by tierKey, and position only orders them
  // within a tier. Insertion order within each array IS position order, which is
  // how cm-api wrote it (ordered by tierKey, then position).
  const slots: SlotDto[] = [];
  for (const [tierKey, seats] of Object.entries(snapshot.slots)) {
    seats.forEach((csmId, position) => {
      if (csmId === null) {
        slots.push({
          tierKey,
          position,
          csmId: null,
          csmDisplayName: null,
          csmActive: null,
          vacancy: true,
        });
        return;
      }
      const member = byId.get(csmId);
      if (!member) {
        // KEPT, not dropped — see the module comment. The tier had this many
        // seats and one of them is now unfilled, which is a different and truer
        // statement than "the tier is smaller".
        departedInSeats.push({ tierKey, csmId });
        slots.push({
          tierKey,
          position,
          csmId: null,
          csmDisplayName: null,
          csmActive: null,
          vacancy: true,
        });
        return;
      }
      slots.push({
        tierKey,
        position,
        csmId: member.id,
        csmDisplayName: member.displayName,
        csmActive: member.active,
        vacancy: false,
      });
    });
  }

  // ── pins ─────────────────────────────────────────────────────────────────
  // The two halves are merged back into one PinDto per customer, because that is
  // the shape the scenario overlay takes: a customer pinned to a person AND to a
  // tier is one decision with two parts, not two pins.
  const customers = new Set([
    ...Object.keys(snapshot.pins),
    ...Object.keys(snapshot.tierPins),
  ]);

  const pins: PinDto[] = [];
  for (const customerId of [...customers].sort()) {
    if (!present.customerIds.has(customerId)) {
      // Pinning a customer the book does not hold would be a decision about
      // nothing. Reported, because "the pin is gone" and "the customer is gone"
      // are different facts and only one of them is about the pin.
      missingCustomers.push(customerId);
      continue;
    }
    const csmId = snapshot.pins[customerId] ?? null;
    const tierKey = snapshot.tierPins[customerId] ?? null;

    if (csmId !== null && !byId.has(csmId)) {
      departedInPins.push({ customerId, csmId });
      // The TIER half survives on its own. A hand-picked placement is a
      // statement about the customer, and it does not stop being true because
      // the person who held it has left.
      if (tierKey === null) continue;
      pins.push(pinDto(customerId, null, tierKey));
      continue;
    }

    const member = csmId === null ? null : (byId.get(csmId) ?? null);
    pins.push(pinDto(customerId, member, tierKey));
  }

  const unresolved = { departedInSeats, departedInPins, missingCustomers };
  return {
    scenario: { slots, pins },
    unresolved,
    clean: isRestoreClean(unresolved),
  };
}

function pinDto(
  customerId: string,
  member: RosterMemberDto | null,
  tierKey: string | null,
): PinDto {
  return {
    customerId,
    // Null when no roster member resolved — a pin naming an id the roster does
    // not have would be refused by the engine, and the caller has already
    // recorded that in `unresolved`.
    csmId: member?.id ?? null,
    csmDisplayName: member?.displayName ?? null,
    csmActive: member?.active ?? null,
    tierKey,
    // A restored pin is the SNAPSHOT's decision replayed, not a new one. Saying
    // so keeps it from reading as something somebody just did.
    reason: 'Restored from a saved scenario',
    createdBy: 'restore',
    createdAt: new Date().toISOString(),
  };
}

/** One line per thing that could not be restored, for the caller to render. */
export function describeUnresolved(
  u: RestoreUnresolved,
  displayName: (csmId: string) => string,
): string[] {
  const lines: string[] = [];
  if (u.departedInSeats.length > 0) {
    const who = [
      ...new Set(u.departedInSeats.map((s) => displayName(s.csmId))),
    ];
    lines.push(
      `${u.departedInSeats.length} ${u.departedInSeats.length === 1 ? 'seat is' : 'seats are'} held by someone no longer on the roster (${who.join(', ')}) — kept as ${u.departedInSeats.length === 1 ? 'a vacancy' : 'vacancies'}, so the tier is not silently smaller than it was.`,
    );
  }
  if (u.departedInPins.length > 0) {
    const who = [...new Set(u.departedInPins.map((p) => displayName(p.csmId)))];
    lines.push(
      `${u.departedInPins.length} ${u.departedInPins.length === 1 ? 'pin names' : 'pins name'} someone no longer on the roster (${who.join(', ')}). Those customers are distributed by the balancer in this scenario; any hand-picked tier was kept.`,
    );
  }
  if (u.missingCustomers.length > 0) {
    lines.push(
      `${u.missingCustomers.length} pinned ${u.missingCustomers.length === 1 ? 'customer is' : 'customers are'} not in the current book and were skipped.`,
    );
  }
  return lines;
}
